import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../../lib/utils.js';

const MAX_MOBILE_LEVEL = 3;
const identity = (anchor) => anchor;

function toPanelId(key) {
  return `asl-mobile-${String(key).replace(/[^A-Za-z0-9_-]/g, '-')}`;
}

/**
 * Ein Knoten des mobilen ASL-Akkordeons (Disclosure-Muster).
 *
 * Layout-Regel (Browser-Befund bei 390 px): Link und Umschalter stehen in EINER Zeile, die Unterliste ist ein
 * Geschwister UNTER dieser Zeile. Lag sie wie zuvor in einem `<details>` als Flex-Kind der Zeile, quetschte die
 * aufgeklappte Liste den Link auf "Gef…".
 *
 * Die Unterliste bleibt im HTML (`hidden`), solange sie geschlossen ist: Links bleiben für SSR, Crawler und
 * Tests sichtbar, ohne die Tastatur-Reihenfolge zu stören.
 */
export function MobileAslNode({ category, level = 1, categoryUrl, onCategoryClick, wrapLink = identity }) {
  const [open, setOpen] = useState(false);
  const children = level < MAX_MOBILE_LEVEL && Array.isArray(category.children) ? category.children : [];
  const hasChildren = children.length > 0;
  const panelId = toPanelId(category.key);

  const anchor = (
    <a
      href={categoryUrl(category)}
      onClick={(event) => onCategoryClick(event, category)}
      data-asl-category-key={category.key}
      data-asl-level={category.level}
      className="flex min-h-8 min-w-0 flex-1 items-center rounded-[var(--radius)] px-3 py-1.5 text-left text-xs font-medium hover:bg-[var(--surface-container-low)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] cursor-pointer"
    >
      <span className="truncate">{category.name}</span>
    </a>
  );

  return (
    <div className="space-y-1" data-asl-mobile-category={category.key}>
      <div className="flex items-center gap-1">
        {wrapLink(anchor)}
        {hasChildren && (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={`Unterkategorien von ${category.name} ${open ? 'ausblenden' : 'anzeigen'}`}
            onClick={() => setOpen((current) => !current)}
            className="flex min-h-8 min-w-8 shrink-0 cursor-pointer items-center justify-center rounded-[var(--radius)] hover:bg-[var(--surface-container-low)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
          >
            <ChevronDown className={cn('h-3 w-3 text-[var(--secondary)] transition-transform', open && 'rotate-180')} />
          </button>
        )}
      </div>
      {hasChildren && (
        <div
          id={panelId}
          role="group"
          aria-label={`Unterkategorien von ${category.name}`}
          hidden={!open}
          className="ml-3 mt-1 border-l border-[var(--border)] pl-2 anim-fade-slide-in"
        >
          {children.map((child) => (
            <MobileAslNode
              key={child.key}
              category={child}
              level={level + 1}
              categoryUrl={categoryUrl}
              onCategoryClick={onCategoryClick}
              wrapLink={wrapLink}
            />
          ))}
        </div>
      )}
    </div>
  );
}
